import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { ControlProps, GroupBase } from 'react-select';
import { components } from 'react-select';
import { twJoin, twMerge } from 'tailwind-merge';

import type { LabeComponentProps } from './types';

export function newContainer({
  label,
  labelProps,
  showBorder = false,
  noBorder = false,
  containerClassName,
}: {
  label?: ReactNode;
  labelProps?: LabeComponentProps;
  showBorder?: boolean;
  noBorder?: boolean;
  containerClassName?: string;
}) {
  const shrink = 'text-[11px] leading-tight';
  function ControlComponent<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >({ className, ...props }: ControlProps<Option, IsMulti, Group>) {
    const value = props.getValue();
    const hideBorder = (!showBorder && props.isDisabled && props.isMulti) || noBorder;

    const hasValue = useMemo(() => {
      let _hasValue = false;
      if (value !== null) {
        if (typeof value === 'object') {
          _hasValue = Object.entries(value).length > 0;
        } else if (typeof value === 'string') {
          _hasValue = (value as string).length > 0;
        } else if (Array.isArray(value)) {
          _hasValue = (value as unknown[]).length > 0;
        }
      }
      // !important
      // making hasVale always true to make the label always shrink
      return _hasValue || true;
    }, [value]);

    return (
      <>
        <div
          className={twMerge(
            `peer w-full rounded-none! border-0! ${hideBorder ? 'border-b-none!' : 'border-b!'} ${props.isDisabled ? 'border-c-border-muted!' : 'border-c-border!'} hover:border-c-border! focus:border-primary! placeholder-shown:border-c-border! bg-transparent! pt-2 outline-hidden transition-all focus:border-none! focus:outline-hidden! focus:outline-0 focus-visible:border-none! focus-visible:outline-hidden!`,
            containerClassName
          )}
        >
          <components.Control
            className={twJoin(className, 'min-h-0! border-none! bg-transparent! shadow-none!')}
            {...props}
          />
        </div>

        <div
          className={twMerge(
            "after:content[''] text-c-card-muted after:border-primary pointer-events-none absolute -top-1.5 left-0 flex size-full transform-gpu truncate overflow-visible! text-sm leading-[4.25] font-normal transition-all select-none after:absolute after:-bottom-1.5 after:block after:w-full after:scale-x-0 after:border-b-2 after:transition-transform after:duration-300",
            'peer-focus-within:text-primary peer-focus-within:after:border-primary dark:peer-focus-within:text-primary peer-focus-within:text-[11px] peer-focus-within:leading-tight peer-focus-within:after:scale-x-100',
            'peer-placeholder-shown:text-accent-light peer-placeholder-shown:peer-disabled:text-accent-light peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.25] dark:peer-placeholder-shown:text-gray-400',
            'peer-disabled:text-accent-light dark:peer-disabled:text-gray-400',
            hasValue && shrink,
            labelProps?.className
          )}
        >
          {label}
        </div>
      </>
    );
  }

  return ControlComponent;
}
